import type { DropdownOption } from '@/components/VNDropdown';
import type { Input } from '@/models/input';
import { FuelEntityType } from '@/services';

export const PROTOCOL_SRT = 'SRT';
export const PROTOCOL_RTMP = 'RTMP';

export const DEFAULT_RESOLUTION = '1080p';

const RESOLUTION_BITRATES: Record<string, number> = {
  '1080p': 7200,
  '720p': 4500,
  '540p': 2500,
};

export const PROTOCOL_OPTIONS: DropdownOption[] = [
  { id: PROTOCOL_SRT, label: 'SRT' },
  { id: PROTOCOL_RTMP, label: 'RTMP (coming soon)', disabled: true },
];

/** Prefer top-level protocol, then metaData, then SRT (current default). */
export const resolveInputProtocol = (
  input?: { protocol?: string | null; metaData?: { protocol?: string | null } | null } | null
): string => {
  const fromTop = input?.protocol?.trim();
  if (fromTop) return fromTop;
  const fromMeta = input?.metaData?.protocol?.trim();
  if (fromMeta) return fromMeta;
  return PROTOCOL_SRT;
};

export const RESOLUTION_OPTIONS: DropdownOption[] = [
  { id: '1080p', label: 'Full HD — 1080p', subtitle: '1920×1080 · 7.2 Mbps' },
  { id: '720p', label: 'HD — 720p', subtitle: '1280×720 · 4.5 Mbps' },
  { id: '540p', label: 'SD+ — 540p', subtitle: '960×540 · 2.5 Mbps' },
];

export const getBitrateKbpsForResolution = (resolution?: string | null): number => {
  if (!resolution) {
    return RESOLUTION_BITRATES[DEFAULT_RESOLUTION];
  }
  return RESOLUTION_BITRATES[resolution] ?? RESOLUTION_BITRATES[DEFAULT_RESOLUTION];
};

export const formatBitrateLabel = (resolution?: string | null): string => {
  return `${getBitrateKbpsForResolution(resolution)} kbps`;
};

export type InputSourceFormValues = Partial<Input> & {
  ouId?: string;
  entityType?: string;
  mode?: 'new' | 'existing';
  resolution?: string;
  bitrateKbps?: number;
  bitrateLabel?: string;
};

export const INPUT_SOURCE_DEFAULT_COLORS = [
  '#4F6BED',
  '#14B8A6',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#22C55E',
  '#2D9CDB',
  '#F97316',
  '#D946EF',
  '#84CC16',
  '#06B6D4',
] as const;

export const getDefaultInputSourceColor = (existingInputCount: number): string =>
  INPUT_SOURCE_DEFAULT_COLORS[existingInputCount % INPUT_SOURCE_DEFAULT_COLORS.length];

export const getDefaultCreateFormValues = (ouId: string, existingInputCount = 0): InputSourceFormValues => ({
  name: '',
  resolution: DEFAULT_RESOLUTION,
  bitrateKbps: getBitrateKbpsForResolution(DEFAULT_RESOLUTION),
  bitrateLabel: formatBitrateLabel(DEFAULT_RESOLUTION),
  enabled: true,
  description: '',
  ouId,
  entityOuId: ouId,
  entityType: FuelEntityType.Input,
  mode: 'new',
  protocol: PROTOCOL_SRT,
  location: '',
  color: getDefaultInputSourceColor(existingInputCount),
  metaData: {
    location: '',
    protocol: PROTOCOL_SRT,
    resolution: DEFAULT_RESOLUTION,
  },
});

export const buildCreatePayload = (form: InputSourceFormValues, ouId: string) => {
  const trimmedName = form.name?.trim() ?? '';
  const trimmedLocation = form?.location?.trim();
  const resolution = form.resolution ?? form?.resolution ?? DEFAULT_RESOLUTION;

  return {
    mode: 'new' as const,
    entityType: FuelEntityType.Input,
    ouId,
    entityOuId: ouId,
    name: trimmedName,
    location: trimmedLocation || '',
    enabled: true,
    description: form.description ?? '',
    protocol: form?.protocol ?? PROTOCOL_SRT,
    resolution,
    bitrateKbps: getBitrateKbpsForResolution(resolution),
    color: (form.color ?? '').trim() || null,
  };
};

export const buildUpdatePayload = (form: InputSourceFormValues, ouId: string) => {
  const trimmedName = form.name?.trim() ?? '';
  const trimmedLocation = form.location?.trim() ?? form.metaData?.location?.trim();
  const resolution = form.resolution ?? form.metaData?.resolution ?? DEFAULT_RESOLUTION;

  return {
    ...form,
    mode: 'existing' as const,
    entityType: FuelEntityType.Input,
    id: form.id,
    ouId,
    entityOuId: form.entityOuId ?? ouId,
    name: trimmedName,
    enabled: form.enabled ?? true,
    protocol: form.protocol ?? form.metaData?.protocol ?? PROTOCOL_SRT,
    location: trimmedLocation || '',
    resolution,
    bitrateKbps: getBitrateKbpsForResolution(resolution),
  };
};
